import React, { useMemo, useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { ModalEditarTarea } from './ModalEditarTarea';
import { TareaConRelaciones, EstadoTarea } from '../types';

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const estadoColorBar: Record<EstadoTarea, string> = {
    backlog: 'bg-slate-400',
    por_hacer: 'bg-blue-500',
    en_progreso: 'bg-amber-500',
    esperando_cliente: 'bg-orange-500',
    testing: 'bg-purple-500',
    produccion: 'bg-emerald-500'
};

const estadoLabel: Record<EstadoTarea, string> = {
    backlog: 'Backlog',
    por_hacer: 'Por Hacer',
    en_progreso: 'En Progreso',
    esperando_cliente: 'Esperando Cliente',
    testing: 'Testing',
    produccion: 'Producción'
};

const DAY_MS = 1000 * 60 * 60 * 24;

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const addDays = (d: Date, n: number): Date => {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
};

interface TimeRange {
    start: Date;
    end: Date;
    totalDays: number;
}

const PRESETS: Array<{ label: string; back: number; forward: number }> = [
    { label: '2 semanas', back: 7,  forward: 14 },
    { label: '1 mes',     back: 14, forward: 30 },
    { label: '3 meses',   back: 30, forward: 60 },
    { label: '6 meses',   back: 30, forward: 150 }
];

export const VistaTimeline: React.FC = () => {
    const { swimlanes, actualizarTarea, eliminarTarea, marcarCompletado } = useKanban();
    const [presetIdx, setPresetIdx] = useState(1);
    const [selectedTarea, setSelectedTarea] = useState<TareaConRelaciones | null>(null);

    const range: TimeRange = useMemo(() => {
        const today = startOfDay(new Date());
        const preset = PRESETS[presetIdx];
        const start = addDays(today, -preset.back);
        const end = addDays(today, preset.forward);
        return { start, end, totalDays: Math.round((end.getTime() - start.getTime()) / DAY_MS) };
    }, [presetIdx]);

    const tareasFlat: TareaConRelaciones[] = useMemo(() => {
        const all: TareaConRelaciones[] = [];
        swimlanes.forEach(sw => {
            Object.values(sw.tareas).forEach(arr => arr.forEach(t => all.push(t)));
        });
        return all;
    }, [swimlanes]);

    const tareasVisibles = useMemo(() => {
        return tareasFlat.filter(t => {
            const inicio = new Date(t.creado_en);
            const fin = t.fecha_limite ? new Date(t.fecha_limite) : new Date();
            return inicio <= range.end && fin >= range.start;
        });
    }, [tareasFlat, range]);

    const gruposPorCliente = useMemo(() => {
        const map = new Map<string, { nombre: string; color: string; tareas: TareaConRelaciones[] }>();
        tareasVisibles.forEach(t => {
            const key = `${t.cliente_id}`;
            const existing = map.get(key);
            if (existing) {
                existing.tareas.push(t);
            } else {
                map.set(key, { nombre: t.empresa, color: t.cliente_color, tareas: [t] });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [tareasVisibles]);

    const monthMarkers = useMemo(() => {
        const markers: Array<{ date: Date; leftPct: number; label: string }> = [];
        const cursor = new Date(range.start.getFullYear(), range.start.getMonth(), 1);
        while (cursor <= range.end) {
            if (cursor >= range.start) {
                const leftPct = ((cursor.getTime() - range.start.getTime()) / (range.end.getTime() - range.start.getTime())) * 100;
                markers.push({
                    date: new Date(cursor),
                    leftPct,
                    label: `${MESES_CORTOS[cursor.getMonth()]} ${cursor.getFullYear()}`
                });
            }
            cursor.setMonth(cursor.getMonth() + 1);
        }
        return markers;
    }, [range]);

    const todayPct = useMemo(() => {
        const today = startOfDay(new Date());
        if (today < range.start || today > range.end) return null;
        return ((today.getTime() - range.start.getTime()) / (range.end.getTime() - range.start.getTime())) * 100;
    }, [range]);

    const barStyle = (t: TareaConRelaciones): React.CSSProperties | null => {
        const inicio = startOfDay(new Date(t.creado_en));
        const finRaw = t.fecha_limite ? startOfDay(new Date(t.fecha_limite)) : startOfDay(new Date());
        const fin = finRaw < inicio ? addDays(inicio, 1) : finRaw;

        const left = Math.max(0, (inicio.getTime() - range.start.getTime()) / DAY_MS);
        const right = Math.min(range.totalDays, (fin.getTime() - range.start.getTime()) / DAY_MS);
        const width = Math.max(0.5, right - left);

        return {
            left: `${(left / range.totalDays) * 100}%`,
            width: `${(width / range.totalDays) * 100}%`
        };
    };

    const formatDay = (d: Date): string =>
        `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Rango</span>
                    {PRESETS.map((p, idx) => (
                        <button
                            key={p.label}
                            onClick={() => setPresetIdx(idx)}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                presetIdx === idx
                                    ? 'bg-slate-900 text-white'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="text-xs text-slate-500">
                    {tareasVisibles.length} tarea{tareasVisibles.length !== 1 ? 's' : ''} · {gruposPorCliente.length} cliente{gruposPorCliente.length !== 1 ? 's' : ''}
                </div>
            </div>

            <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                    <div className="grid grid-cols-[160px_1fr] border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
                        <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Cliente</div>
                        <div className="relative h-10">
                            {monthMarkers.map((m, i) => (
                                <div
                                    key={i}
                                    className="absolute top-0 bottom-0 border-l border-slate-300 pl-1.5 pt-1 text-[10px] font-semibold uppercase text-slate-500"
                                    style={{ left: `${m.leftPct}%` }}
                                >
                                    {m.label}
                                </div>
                            ))}
                            {todayPct !== null && (
                                <div
                                    className="absolute top-0 bottom-0 border-l-2 border-blue-500"
                                    style={{ left: `${todayPct}%` }}
                                    title="Hoy"
                                >
                                    <span className="absolute -top-0.5 left-1 px-1 text-[9px] font-bold text-blue-700 bg-white rounded">HOY</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {gruposPorCliente.length === 0 && (
                        <div className="text-center text-slate-400 italic py-12">
                            No hay tareas en este rango.
                        </div>
                    )}

                    {gruposPorCliente.map(grupo => (
                        <div key={grupo.nombre} className="border-b border-slate-100 last:border-b-0">
                            <div className="grid grid-cols-[160px_1fr] items-stretch hover:bg-slate-50/50">
                                <div className="px-3 py-3 flex items-center gap-2 border-r border-slate-100">
                                    <span
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: grupo.color }}
                                    />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-slate-800 line-clamp-1">{grupo.nombre}</div>
                                        <div className="text-[10px] text-slate-500">{grupo.tareas.length} tarea{grupo.tareas.length !== 1 ? 's' : ''}</div>
                                    </div>
                                </div>
                                <div className="relative py-3 px-1" style={{ minHeight: '52px' }}>
                                    {monthMarkers.map((m, i) => (
                                        <div
                                            key={i}
                                            className="absolute top-0 bottom-0 border-l border-slate-100"
                                            style={{ left: `${m.leftPct}%` }}
                                        />
                                    ))}
                                    {todayPct !== null && (
                                        <div
                                            className="absolute top-0 bottom-0 border-l border-blue-300 border-dashed"
                                            style={{ left: `${todayPct}%` }}
                                        />
                                    )}
                                    <div className="relative space-y-1.5">
                                        {grupo.tareas.map(t => {
                                            const style = barStyle(t);
                                            if (!style) return null;
                                            const overdue = t.fecha_limite && new Date(t.fecha_limite) < new Date() && t.estado !== 'produccion';
                                            return (
                                                <button
                                                    key={t.id}
                                                    onClick={() => setSelectedTarea(t)}
                                                    style={style}
                                                    className={`absolute h-6 rounded-md ${estadoColorBar[t.estado]} ${
                                                        overdue ? 'ring-2 ring-red-500 ring-offset-1' : ''
                                                    } hover:opacity-80 hover:scale-y-110 transition-transform cursor-pointer shadow-sm flex items-center px-2 overflow-hidden`}
                                                    title={`${t.titulo}\n${formatDay(new Date(t.creado_en))} → ${t.fecha_limite ? formatDay(new Date(t.fecha_limite)) : 'sin límite'}\n${estadoLabel[t.estado]} · ${t.cliente_nombre}`}
                                                >
                                                    <span className="text-[10px] font-medium text-white truncate">
                                                        {t.titulo}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-t border-slate-200 bg-slate-50 text-[11px]">
                <span className="text-slate-500 font-semibold">Estados:</span>
                {(Object.keys(estadoColorBar) as EstadoTarea[]).map(e => (
                    <span key={e} className="inline-flex items-center gap-1">
                        <span className={`w-2.5 h-2.5 rounded-sm ${estadoColorBar[e]}`} />
                        <span className="text-slate-600">{estadoLabel[e]}</span>
                    </span>
                ))}
                <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-sm bg-slate-400 ring-2 ring-red-500 ring-offset-1" />
                    <span className="text-slate-600">Vencida</span>
                </span>
            </div>

            {selectedTarea && (
                <ModalEditarTarea
                    tarea={selectedTarea}
                    onClose={() => setSelectedTarea(null)}
                    onActualizar={actualizarTarea}
                    onEliminar={eliminarTarea}
                    onMarcarCompletado={marcarCompletado}
                />
            )}
        </div>
    );
};